import React from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faArrowDown } from '@fortawesome/free-solid-svg-icons';
import { heroCopy, toolkit } from '../data/content';
import { Button } from './ui/button';

const lineVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.14, delayChildren: 0.15 } },
};

const wordVariants = {
  hidden: { opacity: 0, y: '100%' },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } },
};

const Hero = () => {
  return (
    <section id="top" className="relative flex min-h-screen snap-start flex-col justify-center overflow-hidden pt-24">
      <div className="pointer-events-none absolute inset-0 bg-hero-mesh" aria-hidden="true" />

      <div className="container relative py-16">
        <motion.div
          className="mb-5 inline-flex items-center gap-2 rounded-full border border-border-strong px-4 py-2 font-mono text-[0.78rem] text-muted"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.6 }}
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent-green opacity-60" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-accent-green" />
          </span>
          {heroCopy.available}
        </motion.div>

        <p className="mb-3 font-mono text-sm uppercase tracking-wide text-primary-light">{heroCopy.eyebrow}</p>

        <motion.h1
          className="max-w-2xl font-display text-[clamp(2.4rem,7vw,5.2rem)] font-bold leading-[1.02] tracking-tight"
          variants={lineVariants}
          initial="hidden"
          animate="show"
        >
          {heroCopy.lines.map((line, i) => (
            <span className="block overflow-hidden" key={i}>
              <motion.span className="block" variants={wordVariants}>
                {i === 1 ? (
                  <span className="bg-gradient-to-r from-primary-light via-primary to-accent-orange bg-clip-text text-transparent">
                    {line}
                  </span>
                ) : (
                  <span className={i === 0 ? 'text-foreground' : 'text-muted'}>{line}</span>
                )}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <motion.p
          className="mt-6 max-w-xl text-[clamp(1.05rem,1.6vw,1.2rem)] leading-relaxed text-muted"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          {heroCopy.sub}
        </motion.p>

        <motion.div
          className="mt-8 flex flex-wrap gap-3"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          <Button asChild size="lg">
            <a href={heroCopy.ctaPrimary.href}>
              {heroCopy.ctaPrimary.label} <FontAwesomeIcon icon={faArrowRight} />
            </a>
          </Button>
          <Button asChild size="lg" variant="outline">
            <a href={heroCopy.ctaSecondary.href}>{heroCopy.ctaSecondary.label}</a>
          </Button>
        </motion.div>
      </div>

      <div className="relative border-y border-border py-3 [mask-image:linear-gradient(90deg,transparent,black_8%,black_92%,transparent)] overflow-hidden">
        <div className="flex w-max animate-marquee">
          {[...toolkit, ...toolkit].map((item, i) => (
            <span
              key={i}
              className="flex shrink-0 items-center gap-4 whitespace-nowrap px-4 font-mono text-[0.8rem] text-foreground/70"
            >
              {item}
              <span className="text-primary-light/70">·</span>
            </span>
          ))}
        </div>
      </div>

      <a
        href="#story"
        className="absolute bottom-6 right-6 hidden h-11 w-11 animate-bounce items-center justify-center rounded-full border border-border-strong text-muted transition-colors hover:text-foreground sm:flex"
        aria-label="Scroll to content"
      >
        <FontAwesomeIcon icon={faArrowDown} />
      </a>
    </section>
  );
};

export default Hero;
